import React, { useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faDownload, faUpload, faSpinner, faCheck, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { useEscapeKey } from "../hooks/useEscapeKey";
import "../styles/DashboardFileModal.css";

// Shared Download/Upload popup for the Dashboard Builder toolbar (JSON config,
// HTML overview, ...). Styled after RedirectModal (survey-scriping) so every
// "manage a file" popup in the app looks and behaves the same way.
const DashboardFileModal = ({
  isOpen,
  onClose,
  title,
  description,
  downloadLabel = "Download",
  downloadHint,
  onDownload,
  downloadDisabled,
  uploadLabel = "Upload",
  uploadHint,
  uploadAccept,
  onUploadFile,
  isUploading,
  uploadStatus, // 'success' | 'error' | null
  lastUpdatedText,
}) => {
  const fileInputRef = useRef(null);

  useEscapeKey(isOpen, onClose);

  if (!isOpen) return null;

  return (
    <div className="dfm-overlay" onClick={onClose}>
      <div className="dfm-content" onClick={(e) => e.stopPropagation()}>
        <div className="dfm-header">
          <h3>{title}</h3>
          <button className="dfm-close-btn" onClick={onClose} aria-label="Close" title="Close">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="dfm-body">
          {description && <p className="dfm-description">{description}</p>}
          {lastUpdatedText && <p className="dfm-meta">{lastUpdatedText}</p>}

          <div className="dfm-action-card">
            <div className="dfm-action-info">
              <span className="dfm-action-title">Download</span>
              <span className="dfm-action-hint">{downloadHint}</span>
            </div>
            <button
              className="dfm-btn dfm-btn--download"
              onClick={onDownload}
              disabled={downloadDisabled}
            >
              <FontAwesomeIcon icon={faDownload} /> {downloadLabel}
            </button>
          </div>

          <div className="dfm-divider"><span>or</span></div>

          <div className="dfm-action-card">
            <div className="dfm-action-info">
              <span className="dfm-action-title">Upload</span>
              <span className="dfm-action-hint">{uploadHint}</span>
            </div>

            <input
              type="file"
              accept={uploadAccept}
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                onUploadFile?.(file);
                e.target.value = "";
              }}
            />
            <button
              className={[
                "dfm-btn",
                "dfm-btn--upload",
                isUploading ? "dfm-btn--busy" : "",
                uploadStatus === "success" ? "dfm-btn--success" : "",
                uploadStatus === "error" ? "dfm-btn--error" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              {isUploading ? (
                <><FontAwesomeIcon icon={faSpinner} spin /> Uploading…</>
              ) : uploadStatus === "success" ? (
                <><FontAwesomeIcon icon={faCheck} /> Uploaded!</>
              ) : uploadStatus === "error" ? (
                <><FontAwesomeIcon icon={faExclamationTriangle} /> Error</>
              ) : (
                <><FontAwesomeIcon icon={faUpload} /> {uploadLabel}</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardFileModal;
